import React, { useState } from 'react';
import { Icon, Tooltip, Select, MenuItem, Box } from '@mui/material';
import { get, size } from 'global/lib/lodash';
import Modal from 'global/components/modal';
import useRetrieveData from 'global/hooks/useRetrieveData';
import ImageGrid from 'global/components/image-grid';
import { Button as Btn } from 'global/components/Button';
import { useTheme } from '@mui/material/styles';
import { stopProp, _size, sortAlphaNum } from 'global/utils';
import { withRouter } from 'react-router-dom';
import { useSupersets } from 'global/hooks/useSupersets';
import { Text } from 'global/components/text';
import { supersetsFromScores } from '../AssessmentScore/utils';

// Lazy: ReportPhotos → PhotoRow → Highlights/create → Form → this file.
const getReportPhotos = () => require('../ReportPhotos').default;
// Lazy: ChecklistScores imports CheckpointForm, which imports this file.
const ScoreListItem = (props) => {
  const { CheckpointScore } = require('../ChecklistScores');
  return <CheckpointScore {...props} />;
};

export const ImageModal = (props) => {
  const theme = useTheme();
  const { superset_id, assessment } = props;

  const [state, setState] = useState({
    superset_id,
  });

  const { supersets: fetchedSupersets } = useSupersets({ assessment, scores: get(props, 'scores') });
  const supersets = size(fetchedSupersets) ? fetchedSupersets : supersetsFromScores(get(props, 'scores', []));

  const { data, loading, error } = useRetrieveData(
    { url: '/realm-photos' },
    {
      params: {
        filter: {
          superset_id: state.superset_id,
          assessment_id: get(assessment, 'id'),
        },
        include: 'supersets,images',
      },
      build: ['realmPhotos'],
      useEffect: true,
      transformData: (da) => {
        da = da.map((d) => ({ ...d, ...get(d, 'images.0', {}), assessment })).sort(sortAlphaNum);
        if (!state.superset_id) {
          return da.filter((d) => !size(d.supersets));
        }
        return da;
      },
    }
  );

  const scoredItem = props.scoreListProps?.pssvCheckpoint ? (
    <Box sx={{ mb: 2 }}>
      <ScoreListItem {...props.scoreListProps} showContent={false} showDrop={false} expandScoreLabel />
    </Box>
  ) : null;

  if (error) {
    return <div style={{ color: theme.palette.error.main }}>{error}</div>;
  }

  return (
    <div>
      {scoredItem}
      <Select displayEmpty style={{ width: '100%' }} value={state.superset_id} onChange={(e) => setState((s) => ({ ...s, superset_id: e.target.value }))}>
        {[{ name: 'None', value: null }, ...supersets].map((s) => (
          <MenuItem key={s.id} value={s.id}>
            {s.name}
          </MenuItem>
        ))}
      </Select>
      <ImageGrid
        folders={data}
        includeSearch={false}
        loading={loading}
        userCanEdit={false}
        hideSuperset
        blankContent={() => <Text>There are no photos here; try changing the filters.</Text>}
        {...props}
      />
    </div>
  );
};

export const ImageSelectModal = withRouter((props) => {
  const { scoreListProps, ...modalProps } = props;
  const [selected, setSelected] = useState({});

  const onSelectedChange = (sel) => {
    setSelected(
      Object.keys(sel)
        .filter((k) => !!sel[k])
        .map((k) => sel[k])
    );
  };

  const onClose = () => {
    props.onClose();
    setSelected({});
  };

  const onSubmit = () => {
    props.onSubmit && props.onSubmit({ images: selected });
    onClose();
  };

  const renderBtn = () => {
    if (!_size(selected)) {
      return { title: 'Add', disabled: true };
    }

    return {
      title: `Add ${_size(selected)}`,
      onClick: onSubmit,
    };
  };

  const IMAGEMODAL = props.useFullPhotos ? getReportPhotos() : ImageModal;
  // All Photos: remount on open so ReportPhotos doesn't keep prior search UI state.
  // Assessment Photos (ImageModal): keep prior mount behavior unchanged.
  const modalContent = <IMAGEMODAL canSelect={true} {...modalProps} scoreListProps={scoreListProps} _location={{ pathname: 'reports/photo' }} onSelectedChange={onSelectedChange} />;

  return (
    <Modal {...modalProps} onClose={onClose} buttons={[renderBtn()]}>
      {props.useFullPhotos ? props.open && modalContent : modalContent}
    </Modal>
  );
});

export const ImageSelectBtn = (props) => {
  const { scoreListProps, ...btnProps } = props;
  const [modal, setModal] = useState({ open: false });

  const showImages = (e) => {
    stopProp(e);
    props.onOpen && props.onOpen();
    setModal({
      ...btnProps,
      scoreListProps,
      open: true,
      fullScreen: true,
      title: props.modalTitle || (props.useFullPhotos ? 'All Photos' : 'Add Photos'),
      maxWidth: 920,
      onClose: () => {
        setModal((m) => ({ ...m, open: false }));
        props.onClose && props.onClose();
      },
    });
  };

  if (!props.useFullPhotos && get(props, 'assessment.realmPhotoCount', 0) === 0) {
    return null;
  }

  const tooltip = props.tooltip
    ? props.tooltip
    : props.useFullPhotos
      ? 'Insert from All Photos'
      : props.disabled
        ? 'Please select a category to access your photos'
        : 'Insert from media already uploaded to the Photos tab';

  return (
    <>
      <Tooltip title={tooltip}>
        <span>
          <Btn {...btnProps} onClick={showImages}>
            <Icon>{props.useFullPhotos ? 'image_search' : 'photo_library'}</Icon>
          </Btn>
        </span>
      </Tooltip>
      <ImageSelectModal {...modal} />
    </>
  );
};
